import React, { useCallback, useEffect, useState } from 'react'
import { Flex, InfoAccordion, RoundTabs, StyledTabProps, Text, useModal } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import useScreenSize from 'hooks/useScreenSize'
import { useTranslation } from 'react-i18next'
import { StyledBiennale } from 'components'
import isEmpty from 'lodash/isEmpty'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import { StakeHeader, StakeTableContent } from '../components'
import { ClassicStakeConfig, ClassicStakeUserConfig, useClassicStake } from '../classicStake.logic'
import StakeInnerTable from '../components/StakeInnerTable'
import { observer } from 'mobx-react-lite'
import { stakeStore } from 'mobxStores/StakeStore'
import { JoinStakeModal, ClaimStakeModal, UnstakeModal, UnstakeModalV2 } from '../widgets'

const StyledDescription = styled(StyledBiennale)`
  font-size: 18px;
`

dayjs.extend(duration)

const Container = styled(Flex)`
  width: 100%;
  padding: 24px;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgroundContrast};
`

const Divider = styled(Flex)`
  margin: 8px 0 16px 0;
  opacity: 0.2;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray6};
`

const TabsWrapper = styled(Flex)`
  background: ${({ theme }) => theme.colors.gray10};
  border-radius: 16px;
  padding: 8px;
`

const stakeStatus: StyledTabProps[] = [
  { label: 'Active', value: 0 },
  { label: 'Inactive', value: 1 }
]

export enum STAKE_OPERATION_TYPE {
  STAKE = 'STAKE',
  UNSTAKE = 'UNSTAKE',
  UNSTAKE_V2 = 'UNSTAKE_V2',
  CLAIM = 'CLAIM'
}

const ClassicStake: React.FC = observer(() => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { isMobile } = useScreenSize()
  const { fetchClassicStakeConfig, activeStakes, inactiveStakes, isLoading, unstake, claimReward } = useClassicStake()
  const [activeTab, setActiveTab] = useState<number>(0)
  const { account } = useWeb3React()

  const headerContent = ['stake', 'token', 'earn', 'apr', 'apy', 'fee', 'burn']
  const loadingData = isLoading

  const [joinStakeModalOpen] = useModal(<JoinStakeModal {...{ fetchClassicStakeConfig }} />, false, null, () => {
    stakeStore.resetStakeStore()
  })
  const [claimStakeModalOpen] = useModal(<ClaimStakeModal />, false, null, () => {
    stakeStore.resetStakeStore()
  })
  const [unstakeModalOpen] = useModal(<UnstakeModal {...{ fetchClassicStakeConfig }} />, false, null, () => {
    stakeStore.resetStakeStore()
  })
  const [unstakeDoubleModalOpen] = useModal(<UnstakeModalV2 {...{ fetchClassicStakeConfig }} />, false, null, () => {
    stakeStore.resetStakeStore()
  })

  const handleStakeOperation = useCallback(
    (type: STAKE_OPERATION_TYPE, stake: ClassicStakeConfig, staker: ClassicStakeUserConfig) => {
      stakeStore.setStake(stake)
      stakeStore.setStaker(staker)
      switch (type) {
        case STAKE_OPERATION_TYPE.STAKE:
          joinStakeModalOpen()
          break
        case STAKE_OPERATION_TYPE.UNSTAKE:
          unstakeModalOpen()
          break
        case STAKE_OPERATION_TYPE.UNSTAKE_V2:
          unstakeDoubleModalOpen()
          break
        case STAKE_OPERATION_TYPE.CLAIM:
          claimStakeModalOpen()
          break
      }
    },
    []
  )

  // Scenario wehen thre is Account
  useEffect(() => {
    fetchClassicStakeConfig(account)
  }, [account])

  const stakesType = activeTab === 0 ? 'ACTIVE' : 'INACTIVE'

  const stakeAccordion = () => {
    const stakes = activeTab === 0 ? activeStakes : inactiveStakes

    if (loadingData) {
      return (
        <Flex fullWidth justifyContent="center" minHeight="100px" alignItems="center">
          <UseAnimations animation={loading} strokeColor={colors.gray6} size={48} />
        </Flex>
      )
    }

    if (isEmpty(stakes) && !loadingData) {
      return (
        <Flex fullWidth justifyContent="center" minHeight="100px" alignItems="center">
          <Text>
            {t('stakingPanel.noStakes', {
              type: stakesType.toLowerCase()
            })}
          </Text>
        </Flex>
      )
    }
    return stakes.map((stakeConfig) => {
      return (
        <InfoAccordion
          key={stakeConfig.stake.contractAddress}
          withBorder
          defaultExpanded
          description={<StakeTableContent {...{ stakeConfig, loadingData }} />}
        >
          <StakeInnerTable
            {...{ stakeConfig, loadingData, unstake, claimReward, fetchClassicStakeConfig, handleStakeOperation }}
          />
        </InfoAccordion>
      )
    })
  }

  const mobileView = (
    <>
      <StyledDescription>{t('stakingPanel.stakes')}</StyledDescription>
      <Container>
        <Flex fullWidth mb="16px" justifyContent="space-between">
          <TabsWrapper>
            <RoundTabs
              withBackground
              tabs={stakeStatus}
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
            />
          </TabsWrapper>
        </Flex>
        <Flex flexDirection="column" gap="16px">
          {stakeAccordion()}
        </Flex>
      </Container>
    </>
  )

  const desktopView = (
    <Container>
      <Flex fullWidth mb="16px" justifyContent="space-between">
        <StyledDescription>{t('stakingPanel.stakes')}</StyledDescription>
        <TabsWrapper>
          <RoundTabs
            withBackground
            tabs={stakeStatus}
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
          />
        </TabsWrapper>
      </Flex>
      <StakeHeader {...{ headerContent }} />
      <Divider />
      <Flex flexDirection="column" gap="16px">
        {stakeAccordion()}
      </Flex>
    </Container>
  )

  return <>{isMobile ? mobileView : desktopView}</>
})

export default ClassicStake
